<template>
  <HiloModal
    :isVisible="isVisible"
    title="Notes"
    :showCloseButton="false"
    :hasFooter="true"
    :footerButtons="footerButtons"
    :bottomRight="bottomRight"
    @close="closeModal"
  >
    <template v-slot:header>
      <div>
        <h4>Notes for {{ targetName }}</h4>
      </div>
    </template>
    <div>
      <form @submit.prevent="saveNewNote">
        <div
          v-for="(note, index) in editableNotes"
          :key="index"
          class="form-group note-container"
        >
          <div v-if="note.isNew">
            <input
              v-model="note.noteSubject"
              class="form-control note-subject-input"
              placeholder="Subject"
              required
            />
            <textarea
              v-model="note.note"
              maxlength="255"
              class="form-control note-textarea"
              rows="3"
              placeholder="Note"
              required
            ></textarea>
            <hr v-if="editableNotes.length > 1" class="note-separator" />
          </div>

          <div v-else>
            <div class="note-subject">{{ note.noteSubject }}</div>
            <div class="note-meta">
              {{ note.createdByUser.firstName }}
              {{ note.createdByUser.lastName }}
              <span v-if="note.noteType"> - {{ note.noteType.name }}</span>
              - {{ note.formattedDate }}
            </div>
            <div class="note-content">{{ note.note }}</div>

            <hr
              v-if="index < editableNotes.length - 1"
              class="note-separator"
            />
          </div>
        </div>
      </form>
    </div>
  </HiloModal>
</template>

<script>
import HiloModal from "src/components/HiloComponents/HiloModal.vue";
import NotesAPIService from "src/servicehandlers/NotesAPIService";
import { useLoginStore } from "@/stores/loginStore";
import dayjs from "dayjs";

const notesAPIService = new NotesAPIService();

export default {
  name: "NotesModalOld",
  components: {
    HiloModal,
  },
  props: {
    isVisible: { type: Boolean, default: false },
    targetName: { type: String, required: true },
    targetUserId: { type: Number, default: null },
    filterTargetUserByNoteType: { type: Boolean, default: false },
    targetTaskId: { type: Number, default: null },
    targetEventId: { type: Number, default: null },
    noteTypeName: { type: String, default: "" },
    bottomRight: { type: Boolean, default: false },
  },
  data() {
    return {
      editableNotes: [],
      noteTypeOptions: [],
      noteType: null,
      currentUser: null,
      isCreatingNewNote: false,
    };
  },
  computed: {
    footerButtons() {
      return [
        {
          label: this.isCreatingNewNote ? "Save Note" : "Add Note",
          type: this.isCreatingNewNote ? "greenCheckButton" : undefined,
          onClick: this.isCreatingNewNote
            ? this.triggerFormSubmit
            : this.addNote,
        },
        { label: "Close", onClick: this.closeModal },
      ];
    },
  },
  async mounted() {
    this.loadUserId();
    if (this.isVisible) {
      await this.fetchNoteTypes();
      await this.fetchNotes();
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.fetchNoteTypes();
        this.fetchNotes();
      }
    },
  },
  methods: {
    loadUserId() {
      const store = useLoginStore();
      this.currentUser = store.getUser;
    },
    async fetchNotes() {
      try {
        let notes;
        if (
          this.targetUserId &&
          this.noteType &&
          this.filterTargetUserByNoteType
        ) {
          notes = await notesAPIService.getByTargetUserAndNoteTypeIds(
            this.targetUserId,
            this.noteType.id,
            this.$router,
          );
        } else if (this.targetUserId && !this.filterTargetUserByNoteType) {
          notes = await notesAPIService.getByTargetUserId(
            this.targetUserId,
            this.$router,
          );
        } else if (this.targetTaskId) {
          notes = await notesAPIService.getByTargetTaskId(
            this.targetTaskId,
            this.$router,
          );
        } else if (this.targetEventId) {
          notes = await notesAPIService.getByTargetEventAndNoteTypeIds(
            this.targetEventId,
            this.noteType.id,
            this.$router,
          );
        }
        this.editableNotes = notes.map((note) => ({
          ...note,
          originalNote: note.note,
          formattedDate: dayjs(note.updatedAt).format("MM/DD/YYYY"),
        }));
      } catch (error) {}
    },
    async fetchNoteTypes() {
      try {
        this.noteTypeOptions = await notesAPIService.getNoteTypeOptions();
        this.noteType = this.noteTypeOptions.find(
          (note) => note.name === this.noteTypeName,
        );
      } catch (error) {}
    },
    addNote() {
      this.isCreatingNewNote = true;
      this.editableNotes.unshift({
        id: null,
        noteSubject: "",
        note: "",
        isNew: true,
        targetUserId: this.targetUserId,
        targetTaskId: this.targetTaskId,
        targetEventId: this.targetEventId,
        createdByUserId: this.currentUser.id,
        noteType: this.noteType,
      });
      this.$nextTick(() => {
        const newNoteTextarea = this.$refs.newNote?.[0];
        if (newNoteTextarea) {
          newNoteTextarea.focus();
        }
      });
    },
    triggerFormSubmit() {
      const form = this.$el.querySelector("form");
      if (form) {
        form.requestSubmit();
      }
    },
    async saveNewNote() {
      const note = this.editableNotes[0];
      if (!note.noteSubject.trim() || !note.note.trim()) return;

      try {
        await notesAPIService.create(
          {
            noteSubject: note.noteSubject,
            note: note.note,
            targetUserId: note.targetUserId,
            targetTaskId: note.targetTaskId,
            targetEventId: note.targetEventId,
            createdByUserId: this.currentUser.id,
            noteTypeId: this.noteType?.id,
          },
          this.$router,
        );

        await this.fetchNotes();
        this.isCreatingNewNote = false;
      } catch (error) {}
    },
    closeModal() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.note-container {
  position: relative;
  margin-bottom: 10px;
}

.note-separator {
  border: none;
  border-top: 1px solid #f4f4f4;
  margin: 10px -15px;
}

.note-subject {
  font-size: 18px;
  color: black;
}

.note-meta {
  font-size: 14px;
  color: #888;
}

.note-content {
  font-size: 14px;
  color: black;
}

.note-subject-input,
.note-textarea {
  font-size: 14px;
  color: black;
  background-color: #d9d9d9;
}

.note-subject-input::placeholder,
.note-textarea::placeholder {
  color: #888;
}

.note-subject-input:focus,
.note-textarea:focus {
  background-color: #d9d9d9;
}
</style>
